'use client';
import { useTurnkey } from '@turnkey/react-wallet-kit';
import { useCallback } from 'react';
import { OauthConnectStash } from './oauth-connect-stash.js';
const POPUP_BLOCKED_REGEX = /failed to open .*login window/i;
const CANCEL_REGEX = /cancel|closed|rejected|user denied/i;
function defaultReturnTo() {
    if (typeof window === 'undefined')
        return '/';
    return window.location.pathname + window.location.search;
}
/**
 * Pre-detect browsers known to block popups. Avoids the wasted try-popup
 * round trip on Safari/mobile (~250ms of crypto + PKCE work that always
 * fails). Mirrors the detection in turnkey-provider's
 * `shouldDefaultToOauthRedirect`. Kept duplicated rather than imported
 * to keep this hook self-contained for tests.
 */
function shouldSkipPopupAttempt() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined')
        return false;
    const ua = navigator.userAgent;
    const isSafari = /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
    const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
    return isSafari || isMobile;
}
/** The Turnkey kit's rejection when `window.open` returns null (popup blocked). */
// ALLOW-UNKNOWN: type-guard helpers that narrow caught exception values
export function isPopupBlockedError(err) {
    if (!(err instanceof Error))
        return false;
    return POPUP_BLOCKED_REGEX.test(err.message);
}
// ALLOW-UNKNOWN: type-guard helpers that narrow caught exception values
function isCancellationError(err) {
    if (!(err instanceof Error))
        return false;
    return CANCEL_REGEX.test(err.message);
}
export function useOauthConnect() {
    const { handleGoogleOauth, handleAppleOauth, handleXOauth, handleAddOauthProvider } = useTurnkey();
    const runOauthConnect = useCallback(async (args) => {
        const returnTo = args.returnTo ?? defaultReturnTo();
        let handler = null;
        if (args.provider === 'google') {
            handler = handleGoogleOauth;
        }
        else if (args.provider === 'apple') {
            handler = handleAppleOauth;
        }
        else if (args.provider === 'x') {
            // X mints a Turnkey-ISSUED OIDC token (iss = oidc.turnkey.com); the
            // backend verifies it via the nonce = sha256(oauthPublicKey) binding,
            // so callers must send the attempt key alongside it to
            // /connect-provider (popup: from `onOauthSuccess`; redirect: from
            // `onOauthRedirect` via the pending-connect bridge).
            handler = handleXOauth;
        }
        if (handler === null) {
            return {
                kind: 'error',
                error: new Error(`Unsupported OAuth provider: ${args.provider}`),
            };
        }
        // Fast-path: on Safari / mobile, skip the popup attempt entirely
        // (it's guaranteed to fail with `Failed to open ... login window.`
        // because window.open is blocked after async PKCE work loses the
        // user-gesture token). Write the stash and redirect directly via
        // `handleAddOauthProvider`, which embeds OAUTH_INTENT_ADD_PROVIDER in
        // the OAuth state param so Turnkey's redirect-return calls
        // `addOauthProvider` (priority #1 in completion.js) instead of
        // `completeOauth` — adding the provider to the EXISTING sub-org
        // without replacing the user's session.
        //
        // X cannot ride `handleAddOauthProvider`'s redirect: its return leg is
        // a code-flow response with no id_token in the hash, so the Morfi-DB
        // row could never be written. Instead X redirects via the login-SHAPED
        // `handleXOauth({ openInPage: true })` — the kit exchanges the code on
        // return and routes the minted token + attempt key through
        // `callbacks.onOauthRedirect`, where turnkey-provider sees the connect
        // stash and diverts into `setPendingConnect` instead of logging in.
        if (shouldSkipPopupAttempt()) {
            OauthConnectStash.write({ provider: args.provider, returnTo });
            if (args.provider === 'x') {
                try {
                    await handler({ openInPage: true });
                    return { kind: 'redirecting' };
                }
                catch (redirectErr) {
                    OauthConnectStash.clear();
                    const error = redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr));
                    return { kind: 'error', error };
                }
            }
            try {
                const addHandler = handleAddOauthProvider;
                // REASON: HandleAddOauthProviderParams.providerName is typed as
                // the OAuthProviders enum, whose runtime value lives in
                // @turnkey/sdk-types (not a direct dep of frontend). At runtime
                // the enum value is exactly the string literal we pass —
                // structurally identical, only nominally different.
                await addHandler({
                    // @ts-expect-error REASON: see comment above (Turnkey OAuthProviders enum is nominally typed but structurally a string union at runtime)
                    providerName: args.provider,
                    openInPage: true,
                });
                return { kind: 'redirecting' };
            }
            catch (redirectErr) {
                OauthConnectStash.clear();
                const error = redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr));
                return { kind: 'error', error };
            }
        }
        let capturedToken = null;
        let capturedProviderName = null;
        let capturedPublicKey = null;
        try {
            await handler({
                openInPage: false,
                onOauthSuccess: (params) => {
                    capturedToken = params.oidcToken;
                    capturedProviderName = params.providerName;
                    capturedPublicKey = params.publicKey;
                },
            });
        }
        catch (err) {
            if (isPopupBlockedError(err)) {
                OauthConnectStash.write({ provider: args.provider, returnTo });
                if (args.provider === 'x') {
                    // Same login-shaped redirect as the pre-detected branch — the
                    // add-provider redirect has no id_token on return for X.
                    try {
                        await handler({ openInPage: true });
                        return { kind: 'redirecting' };
                    }
                    catch (redirectErr) {
                        OauthConnectStash.clear();
                        const error = redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr));
                        return { kind: 'error', error };
                    }
                }
                try {
                    // Use handleAddOauthProvider (NOT the login handler) so the
                    // redirect-return calls addOauthProvider on the existing
                    // sub-org instead of completeOauth. Otherwise we'd silently
                    // replace the user's session with a new one for the same
                    // identity. Same path as the pre-detected Safari/mobile branch.
                    const addHandler = handleAddOauthProvider;
                    await addHandler({
                        // @ts-expect-error REASON: see top-of-file note about the
                        // OAuthProviders enum being structurally a string union at
                        // runtime.
                        providerName: args.provider,
                        openInPage: true,
                    });
                    // Page should have navigated away already; if we're still here,
                    // treat it as redirecting and let the caller stop spinning.
                    return { kind: 'redirecting' };
                }
                catch (redirectErr) {
                    // Clean up stash if the redirect call itself failed before navigation.
                    OauthConnectStash.clear();
                    const error = redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr));
                    return { kind: 'error', error };
                }
            }
            if (isCancellationError(err)) {
                return { kind: 'cancelled' };
            }
            const error = err instanceof Error ? err : new Error(String(err));
            return { kind: 'error', error };
        }
        const finalToken = capturedToken;
        const finalProviderName = capturedProviderName;
        const finalPublicKey = capturedPublicKey;
        if (finalToken === null || finalProviderName === null || finalPublicKey === null) {
            // Promise resolved without firing onOauthSuccess — treat as cancelled.
            return { kind: 'cancelled' };
        }
        return {
            kind: 'popup-success',
            oidcToken: finalToken,
            providerName: finalProviderName,
            publicKey: finalPublicKey,
        };
    }, [handleGoogleOauth, handleAppleOauth, handleXOauth, handleAddOauthProvider]);
    return { runOauthConnect };
}
//# sourceMappingURL=use-oauth-connect.js.map